import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert, ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import BuildOutlinedIcon from "@material-ui/icons/BuildOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";

import WhatsappTemplatePreview from "../WhatsappTemplatePreview";
import useWhatsappTemplates from "../../hooks/useWhatsappTemplates";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE,
  normalizeWhatsAppTemplateLanguage,
  resolveWhatsAppTemplateLanguageOptions,
} from "../../constants/whatsappTemplateLanguages";
import {
  AUTH_CODE_EXPIRATION_OPTIONS,
  BUTTON_TYPES,
  EMPTY_TEMPLATE_FORM,
  HEADER_FORMATS,
  TEMPLATE_LIMITS,
  appendVariable,
  buildAuthPreviewParts,
  buildComponentsFromForm,
  canAddButtonType,
  collectFormVariableIndexes,
  collectFormVariableTexts,
  getButtonMode,
  getHeaderMediaAccept,
  getNextVariableIndex,
  headerHasVariables,
  isAllowedHeaderMediaFile,
  isMediaHeaderFormat,
  isTemplateFormValid,
  parseComponentsToForm,
} from "../../utils/templateForm";
import {
  evaluateTemplateCategoryHintsFromForm,
  translateTemplateCategoryHints,
} from "../../utils/templateCategoryHints";
import {
  getTemplateFieldIssueMessage,
  getTemplateSubmitHints,
  translateTemplateSubmitIssues,
  validateTemplateForMetaSubmit,
} from "../../utils/templateSubmitValidation";

const useStyles = makeStyles((theme) => ({
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    paddingRight: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  titleIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 193, 7, 0.12)",
    color: "#FFC107",
    flexShrink: 0,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  sectionLabel: {
    fontSize: "0.82rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  categoryGroup: {
    width: "100%",
    marginBottom: theme.spacing(2),
    display: "flex",
    borderRadius: 8,
    overflow: "hidden",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.04)"
        : "rgba(0, 0, 0, 0.04)",
    "& .MuiToggleButtonGroup-grouped": {
      flex: "1 1 0",
      minWidth: 0,
      margin: 0,
      border: "none",
      borderRadius: "0 !important",
      "&:not(:first-child)": {
        borderLeft:
          theme.palette.type === "dark"
            ? "1px solid rgba(255, 255, 255, 0.12)"
            : "1px solid rgba(0, 0, 0, 0.12)",
      },
    },
  },
  categoryButton: {
    flex: 1,
    minWidth: 0,
    textTransform: "none",
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.35,
    padding: theme.spacing(1.25, 1),
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.45)"
        : theme.palette.text.secondary,
    backgroundColor: "transparent",
    transition: theme.transitions.create(["background-color", "color"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(0, 0, 0, 0.04)",
    },
    "&.Mui-selected": {
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.14)"
          : "#FFFFFF",
      fontWeight: 600,
      "&:hover": {
        backgroundColor:
          theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.18)"
            : "#FFFFFF",
      },
    },
  },
  categoryButtonInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.75),
    width: "100%",
  },
  categoryIcon: {
    fontSize: 18,
    flexShrink: 0,
  },
  variableRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(1.5),
  },
  buttonRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  buttonCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
  },
  buttonDelete: {
    marginTop: theme.spacing(1),
  },
  buttonVariableRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(-0.5),
  },
  counter: {
    textAlign: "right",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(1.5),
  },
  previewColumn: {
    position: "sticky",
    top: 0,
  },
  authInfoBox: {
    padding: theme.spacing(2),
    borderRadius: 12,
    background: "rgba(255, 193, 7, 0.08)",
    border: "1px solid rgba(255, 193, 7, 0.18)",
    marginBottom: theme.spacing(2),
  },
  authInfoTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.75),
  },
  buttonTypeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  carouselNote: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    background: "rgba(255,255,255,0.04)",
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
    marginBottom: theme.spacing(2),
  },
  mediaSampleBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.04)"
        : "rgba(0, 0, 0, 0.02)",
  },
  mediaSampleName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  mediaSampleHint: {
    display: "block",
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  validationAlert: {
    marginBottom: theme.spacing(2),
    "& ul": {
      margin: theme.spacing(1, 0, 0),
      paddingLeft: theme.spacing(2.5),
    },
  },
  variableExampleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  variableExampleToken: {
    minWidth: 48,
    fontWeight: 600,
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
}));

const CATEGORY_OPTIONS = [
  {
    value: "MARKETING",
    icon: LocalOfferOutlinedIcon,
  },
  {
    value: "UTILITY",
    icon: BuildOutlinedIcon,
  },
  {
    value: "AUTHENTICATION",
    icon: LockOutlinedIcon,
  },
];
const META_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;

function WhatsappTemplateDialog({
  open,
  onClose,
  templateSelected,
  cloudLines,
  onSave,
  loadingSave,
}) {
  const classes = useStyles();
  const { uploadSample } = useWhatsappTemplates();
  const bodyInputRef = useRef(null);
  const headerInputRef = useRef(null);
  const headerMediaInputRef = useRef(null);
  const isEdit = Boolean(templateSelected?.id);

  const [name, setName] = useState("");
  const [language, setLanguage] = useState(DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE);
  const [category, setCategory] = useState("UTILITY");
  const [whatsappId, setWhatsappId] = useState("");
  const [form, setForm] = useState(EMPTY_TEMPLATE_FORM);
  const [uploadingHeaderMedia, setUploadingHeaderMedia] = useState(false);

  useEffect(() => {
    if (!open) return;

    setName(templateSelected?.name ?? "");
    setLanguage(
      normalizeWhatsAppTemplateLanguage(
        templateSelected?.language ?? DEFAULT_WHATSAPP_TEMPLATE_LANGUAGE,
      ),
    );
    setCategory(templateSelected?.category ?? "UTILITY");
    setWhatsappId(
      templateSelected?.whatsappId
        ? String(templateSelected.whatsappId)
        : cloudLines[0]?.id
          ? String(cloudLines[0].id)
          : "",
    );

    if (templateSelected?.components) {
      setForm(
        parseComponentsToForm(
          templateSelected.components,
          templateSelected.category ?? category,
        ),
      );
    } else if (templateSelected?.bodyPreview) {
      setForm({ ...EMPTY_TEMPLATE_FORM, bodyText: templateSelected.bodyPreview });
    } else {
      setForm({ ...EMPTY_TEMPLATE_FORM, buttons: [] });
    }
  }, [open, templateSelected, cloudLines]);

  const selectedLine = useMemo(
    () => cloudLines.find((line) => String(line.id) === String(whatsappId)),
    [cloudLines, whatsappId],
  );

  const isAuth = category === "AUTHENTICATION";

  const previewComponents = useMemo(
    () => buildComponentsFromForm(form, category, language),
    [form, category, language],
  );

  const authPreviewParts = useMemo(
    () => (isAuth ? buildAuthPreviewParts(form, language) : null),
    [form, isAuth, language],
  );

  const buttonMode = getButtonMode(form.buttons);
  const isMarketing = category === "MARKETING";
  const headerFormat = String(form.headerFormat ?? "TEXT").toUpperCase();
  const showTextHeader = headerFormat === "TEXT";
  const showMediaHeader = isMediaHeaderFormat(headerFormat);

  const nameError =
    name.trim() && !META_NAME_PATTERN.test(name.trim())
      ? i18n.t("whatsappTemplates.dialog.nameInvalid")
      : "";

  const submitIssues = useMemo(
    () => (isAuth ? [] : validateTemplateForMetaSubmit(previewComponents)),
    [isAuth, previewComponents],
  );

  const submitIssueMessages = useMemo(
    () => translateTemplateSubmitIssues(submitIssues),
    [submitIssues],
  );

  const languageHints = useMemo(
    () => getTemplateSubmitHints({ language }),
    [language],
  );

  const languageOptions = useMemo(
    () => resolveWhatsAppTemplateLanguageOptions(templateSelected?.language),
    [templateSelected?.language],
  );

  const languageSelectDisabled =
    isEdit && !["DRAFT", "REJECTED"].includes(templateSelected?.status);

  const categoryHintMessages = useMemo(() => {
    if (isAuth) return [];
    return translateTemplateCategoryHints(
      evaluateTemplateCategoryHintsFromForm(category, form),
    );
  }, [isAuth, category, form]);

  const variableIndexes = useMemo(() => collectFormVariableIndexes(form), [form]);

  const headerFieldError =
    (headerHasVariables(form.headerText)
      ? i18n.t("whatsappTemplates.validation.template_header_variables_not_allowed")
      : "") || getTemplateFieldIssueMessage(submitIssues, "header");
  const headerMediaFieldError =
    (showMediaHeader && !form.headerMediaUrl?.trim()
      ? i18n.t("whatsappTemplates.validation.template_media_sample_required")
      : "") || getTemplateFieldIssueMessage(submitIssues, "headerMedia");
  const bodyFieldError = getTemplateFieldIssueMessage(submitIssues, "body");
  const buttonsFieldError = getTemplateFieldIssueMessage(submitIssues, "buttons");
  const variableExamplesError = getTemplateFieldIssueMessage(submitIssues, "variableExamples");

  const canSave =
    name.trim() &&
    META_NAME_PATTERN.test(name.trim()) &&
    language.trim() &&
    category &&
    whatsappId &&
    isTemplateFormValid(form, category) &&
    !loadingSave;

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleHeaderFormatChange = (nextFormat) => {
    if (!nextFormat || nextFormat === headerFormat) return;
    updateForm({
      headerFormat: nextFormat,
      headerMediaUrl: "",
      headerMediaFileName: "",
      ...(nextFormat === "TEXT" ? {} : { headerText: "" }),
    });
  };

  const handleHeaderMediaPick = () => {
    headerMediaInputRef.current?.click();
  };

  const handleHeaderMediaSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!isAllowedHeaderMediaFile(headerFormat, file)) {
      toast.error(i18n.t("whatsappTemplates.validation.template_media_sample_invalid_type"));
      return;
    }

    setUploadingHeaderMedia(true);
    try {
      const result = await uploadSample(file);
      updateForm({
        headerMediaUrl: result.url,
        headerMediaFileName: result.fileName || file.name,
      });
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingHeaderMedia(false);
    }
  };

  const handleClearHeaderMedia = () => {
    updateForm({ headerMediaUrl: "", headerMediaFileName: "" });
  };

  const handleVariableExampleChange = (index, value) => {
    updateForm({
      variableExamples: {
        ...(form.variableExamples ?? {}),
        [index]: value,
      },
    });
  };

  const handleAddVariable = (target, buttonIndex = null) => {
    const nextIndex = getNextVariableIndex(...collectFormVariableTexts(form));
    if (target === "url" && buttonIndex != null) {
      const buttons = [...(form.buttons ?? [])];
      const button = buttons[buttonIndex];
      if (!button) return;
      buttons[buttonIndex] = {
        ...button,
        url: appendVariable(button.url ?? "", nextIndex),
      };
      updateForm({ buttons });
      return;
    }
    updateForm({ bodyText: appendVariable(form.bodyText, nextIndex) });
    bodyInputRef.current?.focus();
  };

  const handleAddButton = (type) => {
    if (!canAddButtonType(form.buttons ?? [], type)) return;

    const defaults = {
      [BUTTON_TYPES.QUICK_REPLY]: { type: BUTTON_TYPES.QUICK_REPLY, text: "" },
      [BUTTON_TYPES.URL]: { type: BUTTON_TYPES.URL, text: "", url: "https://" },
      [BUTTON_TYPES.PHONE_NUMBER]: {
        type: BUTTON_TYPES.PHONE_NUMBER,
        text: "",
        phoneNumber: "+",
      },
    };

    updateForm({
      buttons: [...(form.buttons ?? []), defaults[type]],
    });
  };

  const handleButtonChange = (index, patch) => {
    const buttons = [...(form.buttons ?? [])];
    buttons[index] = { ...buttons[index], ...patch };
    updateForm({ buttons });
  };

  const handleRemoveButton = (index) => {
    updateForm({
      buttons: (form.buttons ?? []).filter((_, itemIndex) => itemIndex !== index),
    });
  };

  const handleSubmit = () => {
    const payload = {
      name: name.trim(),
      language: normalizeWhatsAppTemplateLanguage(language),
      category,
      whatsappId: Number(whatsappId),
      components: buildComponentsFromForm(form, category, language),
    };
    onSave(payload, isEdit ? templateSelected.id : null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogContent dividers>
        <div className={classes.titleRow}>
          <div className={classes.titleIcon}>
            <DescriptionOutlinedIcon style={{ fontSize: 20 }} />
          </div>
          <div>
            <Typography variant="h6">
              {isEdit
                ? i18n.t("whatsappTemplates.dialog.editTitle")
                : i18n.t("whatsappTemplates.dialog.addTitle")}
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
              {isAuth
                ? i18n.t("whatsappTemplates.dialog.authSubtitle")
                : i18n.t("whatsappTemplates.dialog.subtitle")}
            </Typography>
          </div>
          <IconButton
            aria-label="close"
            onClick={onClose}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Typography className={classes.sectionLabel}>
              {i18n.t("whatsappTemplates.dialog.category")}
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={category}
              onChange={(_, value) => value && setCategory(value)}
              className={classes.categoryGroup}
              disabled={isEdit}
            >
              {CATEGORY_OPTIONS.map(({ value, icon: CategoryIcon }) => (
                <ToggleButton key={value} value={value} className={classes.categoryButton}>
                  <span className={classes.categoryButtonInner}>
                    <CategoryIcon className={classes.categoryIcon} />
                    {i18n.t(`whatsappTemplates.categories.${value}`)}
                  </span>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <TextField
              className={classes.field}
              label={i18n.t("whatsappTemplates.dialog.name")}
              helperText={nameError || i18n.t("whatsappTemplates.dialog.nameHint")}
              error={Boolean(nameError)}
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              variant="outlined"
              fullWidth
              disabled={isEdit && !["DRAFT", "REJECTED"].includes(templateSelected?.status)}
            />

            <FormControl
              variant="outlined"
              fullWidth
              className={classes.field}
              required
              disabled={languageSelectDisabled}
            >
              <InputLabel>{i18n.t("whatsappTemplates.dialog.language")}</InputLabel>
              <Select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                label={i18n.t("whatsappTemplates.dialog.language")}
              >
                {languageOptions.map(({ value, legacy }) => (
                  <MenuItem key={value} value={value}>
                    {legacy
                      ? i18n.t("whatsappTemplates.templateLanguages.legacy", { code: value })
                      : i18n.t(`whatsappTemplates.templateLanguages.${value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" fullWidth className={classes.field} disabled={isEdit}>
              <InputLabel>{i18n.t("whatsappTemplates.dialog.line")}</InputLabel>
              <Select
                value={whatsappId}
                onChange={(e) => setWhatsappId(e.target.value)}
                label={i18n.t("whatsappTemplates.dialog.line")}
              >
                {cloudLines.map((line) => (
                  <MenuItem key={line.id} value={String(line.id)}>
                    {line.name || `#${line.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isAuth ? (
              <>
                <Box className={classes.authInfoBox}>
                  <Typography className={classes.authInfoTitle}>
                    {i18n.t("whatsappTemplates.dialog.authTitle")}
                  </Typography>
                  <Typography variant="body2">
                    {i18n.t("whatsappTemplates.dialog.authInfo")}
                  </Typography>
                </Box>

                <FormControlLabel
                  className={classes.field}
                  control={
                    <Switch
                      color="primary"
                      checked={form.addSecurityRecommendation}
                      onChange={(event) =>
                        updateForm({ addSecurityRecommendation: event.target.checked })
                      }
                    />
                  }
                  label={i18n.t("whatsappTemplates.dialog.authSecurityRecommendation")}
                />

                <FormControl variant="outlined" fullWidth className={classes.field}>
                  <InputLabel>{i18n.t("whatsappTemplates.dialog.authCodeExpiration")}</InputLabel>
                  <Select
                    value={form.codeExpirationMinutes}
                    onChange={(event) =>
                      updateForm({ codeExpirationMinutes: Number(event.target.value) })
                    }
                    label={i18n.t("whatsappTemplates.dialog.authCodeExpiration")}
                  >
                    {AUTH_CODE_EXPIRATION_OPTIONS.map((minutes) => (
                      <MenuItem key={minutes} value={minutes}>
                        {i18n.t("whatsappTemplates.dialog.authCodeExpirationOption", {
                          minutes,
                        })}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("whatsappTemplates.dialog.authCodeExpirationHint")}
                  </Typography>
                </FormControl>
              </>
            ) : (
              <>
                {languageHints.length > 0 ? (
                  <Alert severity="info" className={classes.validationAlert}>
                    {languageHints.join(" ")}
                  </Alert>
                ) : null}

                {categoryHintMessages.length > 0 ? (
                  <Alert severity="warning" className={classes.validationAlert}>
                    <Typography variant="subtitle2" gutterBottom>
                      {i18n.t("whatsappTemplates.categoryHints.title")}
                    </Typography>
                    <ul>
                      {categoryHintMessages.map((message) => (
                        <li key={message}>
                          <Typography variant="body2">{message}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Alert>
                ) : null}

                {submitIssueMessages.length > 0 ? (
                  <Alert severity="error" className={classes.validationAlert}>
                    <Typography variant="subtitle2" gutterBottom>
                      {i18n.t("whatsappTemplates.validation.title")}
                    </Typography>
                    <ul>
                      {submitIssueMessages.map((message) => (
                        <li key={message}>
                          <Typography variant="body2">{message}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Alert>
                ) : null}

                {isMarketing ? (
                  <Typography className={classes.carouselNote}>
                    {i18n.t("whatsappTemplates.dialog.carouselComingSoon")}
                  </Typography>
                ) : null}

                <Typography className={classes.sectionLabel}>
                  {i18n.t("whatsappTemplates.dialog.headerOptional")}
                </Typography>
                <FormControl variant="outlined" fullWidth className={classes.field}>
                  <InputLabel>{i18n.t("whatsappTemplates.dialog.headerFormat")}</InputLabel>
                  <Select
                    value={headerFormat}
                    onChange={(event) => handleHeaderFormatChange(event.target.value)}
                    label={i18n.t("whatsappTemplates.dialog.headerFormat")}
                  >
                    {HEADER_FORMATS.map((format) => (
                      <MenuItem key={format} value={format}>
                        {i18n.t(`whatsappTemplates.dialog.headerFormats.${format}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {showTextHeader ? (
                  <>
                    <TextField
                      inputRef={headerInputRef}
                      className={classes.field}
                      label={i18n.t("whatsappTemplates.dialog.headerText")}
                      value={form.headerText}
                      onChange={(e) =>
                        updateForm({
                          headerText: e.target.value.slice(0, TEMPLATE_LIMITS.header),
                        })
                      }
                      variant="outlined"
                      fullWidth
                      error={Boolean(headerFieldError)}
                      helperText={
                        headerFieldError ||
                        i18n.t("whatsappTemplates.dialog.headerTextHint")
                      }
                    />
                    <div className={classes.counter}>
                      {(form.headerText?.length ?? 0)}/{TEMPLATE_LIMITS.header}
                    </div>
                  </>
                ) : null}

                {showMediaHeader ? (
                  <>
                    <input
                      ref={headerMediaInputRef}
                      type="file"
                      accept={getHeaderMediaAccept(headerFormat)}
                      onChange={handleHeaderMediaSelect}
                      style={{ display: "none" }}
                    />
                    <div
                      className={classes.mediaSampleBox}
                      style={
                        headerMediaFieldError
                          ? { borderColor: "#f44336" }
                          : undefined
                      }
                    >
                      <AttachFileIcon color="action" fontSize="small" />
                      <Typography variant="body2" className={classes.mediaSampleName}>
                        {form.headerMediaFileName ||
                          i18n.t("whatsappTemplates.dialog.headerMediaEmpty")}
                      </Typography>
                      {form.headerMediaUrl ? (
                        <IconButton
                          size="small"
                          aria-label="remove header media"
                          onClick={handleClearHeaderMedia}
                          disabled={uploadingHeaderMedia}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={handleHeaderMediaPick}
                          disabled={uploadingHeaderMedia}
                        >
                          {uploadingHeaderMedia
                            ? i18n.t("whatsappTemplates.dialog.headerMediaUploading")
                            : i18n.t("whatsappTemplates.dialog.headerMediaAttach")}
                        </Button>
                      )}
                    </div>
                    <Typography
                      variant="caption"
                      className={classes.mediaSampleHint}
                      color={headerMediaFieldError ? "error" : "textSecondary"}
                    >
                      {headerMediaFieldError ||
                        i18n.t("whatsappTemplates.dialog.headerMediaUrlHint")}
                    </Typography>
                  </>
                ) : null}

                <Typography className={classes.sectionLabel}>
                  {i18n.t("whatsappTemplates.dialog.body")} *
                </Typography>
                <TextField
                  inputRef={bodyInputRef}
                  className={classes.field}
                  value={form.bodyText}
                  onChange={(e) =>
                    updateForm({
                      bodyText: e.target.value.slice(0, TEMPLATE_LIMITS.body),
                    })
                  }
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={5}
                  error={Boolean(bodyFieldError)}
                  helperText={bodyFieldError || " "}
                />
                <div className={classes.variableRow}>
                  <Button size="small" onClick={() => handleAddVariable("body")}>
                    {i18n.t("whatsappTemplates.dialog.addVariable")}
                  </Button>
                </div>
                <div className={classes.counter}>
                  {(form.bodyText?.length ?? 0)}/{TEMPLATE_LIMITS.body}
                </div>

                {variableIndexes.length > 0 ? (
                  <>
                    <Typography className={classes.sectionLabel}>
                      {i18n.t("whatsappTemplates.dialog.variableExamplesTitle")}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={variableExamplesError ? "error" : "textSecondary"}
                      display="block"
                      style={{ marginBottom: 12 }}
                    >
                      {variableExamplesError ||
                        i18n.t("whatsappTemplates.dialog.variableExamplesHint")}
                    </Typography>
                    {variableIndexes.map((index) => {
                      const exampleValue = form.variableExamples?.[index] ?? "";
                      const missing = !exampleValue.trim();
                      return (
                        <div key={`var-example-${index}`} className={classes.variableExampleRow}>
                          <span className={classes.variableExampleToken}>{`{{${index}}}`}</span>
                          <TextField
                            value={exampleValue}
                            onChange={(e) => handleVariableExampleChange(index, e.target.value)}
                            variant="outlined"
                            fullWidth
                            required
                            error={missing}
                            placeholder={i18n.t(
                              "whatsappTemplates.dialog.variableExamplePlaceholder",
                              { index },
                            )}
                          />
                        </div>
                      );
                    })}
                  </>
                ) : null}

                <Typography className={classes.sectionLabel}>
                  {i18n.t("whatsappTemplates.dialog.footerOptional")}
                </Typography>
                <TextField
                  className={classes.field}
                  label={i18n.t("whatsappTemplates.dialog.footerText")}
                  value={form.footerText}
                  onChange={(e) =>
                    updateForm({
                      footerText: e.target.value.slice(0, TEMPLATE_LIMITS.footer),
                    })
                  }
                  variant="outlined"
                  fullWidth
                />
                <div className={classes.counter}>
                  {(form.footerText?.length ?? 0)}/{TEMPLATE_LIMITS.footer}
                </div>

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>
                    {i18n.t("whatsappTemplates.dialog.buttonsOptional", {
                      count: form.buttons?.length ?? 0,
                      max:
                        buttonMode === "CTA"
                          ? TEMPLATE_LIMITS.maxCtaButtons
                          : TEMPLATE_LIMITS.maxQuickReplyButtons,
                    })}
                  </Typography>
                </Box>

                {buttonMode || buttonsFieldError ? (
                  <Typography
                    variant="caption"
                    color={buttonsFieldError ? "error" : "textSecondary"}
                    display="block"
                    style={{ marginBottom: 12 }}
                  >
                    {buttonsFieldError ||
                      (buttonMode === "CTA"
                        ? i18n.t("whatsappTemplates.dialog.ctaButtonsHint")
                        : i18n.t("whatsappTemplates.dialog.quickReplyButtonsHint"))}
                  </Typography>
                ) : null}

                <div className={classes.buttonTypeRow}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddButton(BUTTON_TYPES.QUICK_REPLY)}
                    disabled={!canAddButtonType(form.buttons ?? [], BUTTON_TYPES.QUICK_REPLY)}
                  >
                    {i18n.t("whatsappTemplates.dialog.addQuickReply")}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddButton(BUTTON_TYPES.URL)}
                    disabled={!canAddButtonType(form.buttons ?? [], BUTTON_TYPES.URL)}
                  >
                    {i18n.t("whatsappTemplates.dialog.addUrlButton")}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddButton(BUTTON_TYPES.PHONE_NUMBER)}
                    disabled={!canAddButtonType(form.buttons ?? [], BUTTON_TYPES.PHONE_NUMBER)}
                  >
                    {i18n.t("whatsappTemplates.dialog.addPhoneButton")}
                  </Button>
                </div>

                {(form.buttons ?? []).map((button, index) => (
                  <div key={`button-${index}`} className={classes.buttonRow}>
                    <div className={classes.buttonCard}>
                      <TextField
                        value={button.text}
                        onChange={(e) =>
                          handleButtonChange(index, {
                            text: e.target.value.slice(0, TEMPLATE_LIMITS.buttonText),
                          })
                        }
                        variant="outlined"
                        fullWidth
                        label={
                          button.type === BUTTON_TYPES.URL
                            ? i18n.t("whatsappTemplates.dialog.urlButtonText")
                            : button.type === BUTTON_TYPES.PHONE_NUMBER
                              ? i18n.t("whatsappTemplates.dialog.phoneButtonText")
                              : i18n.t("whatsappTemplates.dialog.quickReplyButton")
                        }
                      />
                      {button.type === BUTTON_TYPES.URL ? (
                        <>
                          <TextField
                            value={button.url ?? ""}
                            onChange={(e) => handleButtonChange(index, { url: e.target.value })}
                            variant="outlined"
                            fullWidth
                            label={i18n.t("whatsappTemplates.dialog.urlButtonUrl")}
                            error={Boolean(buttonsFieldError)}
                            helperText={
                              buttonsFieldError ||
                              i18n.t("whatsappTemplates.dialog.urlButtonUrlHint")
                            }
                          />
                          <div className={classes.buttonVariableRow}>
                            <Button size="small" onClick={() => handleAddVariable("url", index)}>
                              {i18n.t("whatsappTemplates.dialog.addVariable")}
                            </Button>
                          </div>
                        </>
                      ) : null}
                      {button.type === BUTTON_TYPES.PHONE_NUMBER ? (
                        <TextField
                          value={button.phoneNumber ?? ""}
                          onChange={(e) =>
                            handleButtonChange(index, { phoneNumber: e.target.value })
                          }
                          variant="outlined"
                          fullWidth
                          label={i18n.t("whatsappTemplates.dialog.phoneButtonNumber")}
                          helperText={i18n.t("whatsappTemplates.dialog.phoneButtonNumberHint")}
                        />
                      ) : null}
                    </div>
                    <IconButton
                      className={classes.buttonDelete}
                      onClick={() => handleRemoveButton(index)}
                      aria-label="remove button"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </div>
                ))}
              </>
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <div className={classes.previewColumn}>
              <WhatsappTemplatePreview
                components={previewComponents}
                businessName={selectedLine?.name}
                authPreviewParts={authPreviewParts}
                variableValues={form.variableExamples ?? {}}
                hintText={
                  isAuth ? null : i18n.t("whatsappTemplates.picker.variablesHint")
                }
              />
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loadingSave}>
          {i18n.t("whatsappTemplates.buttons.cancel")}
        </Button>
        <Button color="primary" variant="contained" onClick={handleSubmit} disabled={!canSave}>
          {i18n.t("whatsappTemplates.buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WhatsappTemplateDialog;
